import React, { useState, useEffect, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  CircularProgress,
  Alert
} from '@mui/material';
import { AssetRiskReportProps } from './types';
import SimulationSelection from './SimulationSelection';
import RiskMetricsDisplay from './RiskMetricsDisplay';
import { calculateAggregateRiskMetrics } from './utils';
import { useSimulations } from '../../api/simulations';
import { useQueries } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';
import { api } from '../../utils/api';

const AssetRiskReportModal: React.FC<AssetRiskReportProps> = ({
  asset,
  open,
  onClose
}) => {
  const [selectedSimulations, setSelectedSimulations] = useState<string[]>([]);
  const [simulationsToLoad, setSimulationsToLoad] = useState<string[]>([]);
  
  const { projectId } = useParams<{ projectId: string }>();

  // Get list of available simulations
  const { data: simResults, isLoading: simLoading } = useSimulations();
  const availableSimulations = useMemo(() => simResults?.data ?? [], [simResults?.data]);
  
  // Fetch simulation results for each simulation individually using useQueries
  const queries = useMemo(() => 
    simulationsToLoad.map(simulationId => ({
      queryKey: ["simulationResults", simulationId],
      queryFn: () =>
        api.request<
          { data: { results: any[]; kpis: { [key: string]: any } }
        }>(
          `/api/projects/${projectId}/fireSimulationResult?simulation_id=${simulationId}`
        ),
      enabled: !!simulationId && !!projectId,
      staleTime: Infinity,
      refetchOnWindowFocus: false,
    })),
    [simulationsToLoad, projectId]
  );
  
  const simulationQueries = useQueries({ queries });
  
  // Collate results from individual queries
  const resultsLoading = simulationQueries.some(query => query.isLoading);
  const resultsError = simulationQueries.find(query => query.error)?.error;
  
  // Calculate risk metrics directly as derived state
  const { riskMetrics, calculationError } = useMemo(() => {
    if (simulationsToLoad.length === 0) {
      return { riskMetrics: null, calculationError: null };
    }
    
    // Collate results from individual queries
    const resultsData = simulationsToLoad.reduce((acc, simulationId, index) => {
      const queryResult = simulationQueries[index];
      if (queryResult?.data?.data) {
        acc[simulationId] = queryResult.data.data;
      }
      return acc;
    }, {} as { [key: string]: any });
    
    // If no data yet, return early
    if (!asset || Object.keys(resultsData).length === 0) {
      return { simulationResultsData: resultsData, riskMetrics: null, calculationError: null };
    }
    
    try {
      // Convert the results data to the expected format
      const validResults = simulationsToLoad.map(simulationId => {
        const resultData = resultsData[simulationId];
        const simulationMetadata = availableSimulations.find(sim => sim.simulation_id === simulationId);
        if (resultData?.results) {
          // Use the first result entry if there are multiple
          const firstResult = resultData.results[0];
          return {
            simulation_id: simulationId,
            id: simulationId,
            name: (simulationMetadata as any)?.name,
            ...firstResult
          };
        }
        return null;
      }).filter(result => result !== null);

      if (validResults.length === 0) {
        return { 
          simulationResultsData: resultsData, 
          riskMetrics: null, 
          calculationError: 'No valid simulation results found' 
        };
      }
      
      // Calculate risk metrics
      const metrics = calculateAggregateRiskMetrics(asset, validResults);
      return { 
        simulationResultsData: resultsData, 
        riskMetrics: metrics, 
        calculationError: null 
      };
    } catch (err) {
      return { 
        simulationResultsData: resultsData, 
        riskMetrics: null, 
        calculationError: 'Error calculating risk metrics: ' + (err instanceof Error ? err.message : 'Unknown error')
      };
    }
  }, [simulationsToLoad, asset, availableSimulations, simulationQueries]);

  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setSelectedSimulations([]);
      setSimulationsToLoad([]);
    }
  }, [open]);

  // Handle load report action
  const handleLoadReport = () => {
    if (selectedSimulations.length > 0) {
      setSimulationsToLoad([...selectedSimulations]);
    }
  };

  const handleSelectionChange = (newSelection: string[]) => {
    setSelectedSimulations(newSelection);
  };

  const assetName = asset?.properties?.name || 'Unnamed Asset';

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xl"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#212b37',
          color: 'white',
          minHeight: 'calc(var(--vh100) * 0.6)'
        }
      }}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" component="div" sx={{ fontWeight: 'bold' }}>
          Asset Risk Report
        </Typography>
        <Typography variant="subtitle1" component="div" sx={{ color: '#888', mt: 1 }}>
          {assetName}
        </Typography>
      </DialogTitle>
      
      <DialogContent sx={{ pb: 1 }}>
        {simLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 200 }}>
            <CircularProgress sx={{ color: '#00d4ad' }} />
            <Typography variant="body1" sx={{ ml: 2 }}>
              Loading simulation data...
            </Typography>
          </Box>
        ) : availableSimulations.length === 0 ? (
          <Alert severity="info" sx={{ mb: 2 }}>
            No simulation data available. Please run some simulations first.
          </Alert>
        ) : (
          <Box sx={{ display: 'flex', gap: 2, minHeight: 'calc(var(--vh100) * 0.6)' }}>
            {/* Left Side - Simulation Selection */}
            <Box sx={{ flex: '0 0 300px' }}>
              <SimulationSelection
                availableSimulations={availableSimulations}
                selectedSimulations={selectedSimulations}
                onSelectionChange={handleSelectionChange}
                onLoadReport={handleLoadReport}
                canLoadReport={!resultsLoading}
              />
            </Box>

            {/* Right Side - Results */}
            <Box sx={{ flex: 1 }}>
              {/* Loading state for metrics calculation */}
              {resultsLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
                  <CircularProgress sx={{ color: '#00d4ad' }} />
                  <Typography variant="body1" sx={{ ml: 2 }}>
                    Calculating risk metrics...
                  </Typography>
                </Box>
              )}

              {/* Error states */}
              {(calculationError || resultsError) && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {calculationError || (resultsError instanceof Error ? resultsError.message : String(resultsError))}
                </Alert>
              )}

              {/* Risk Metrics Display */}
              {!resultsLoading && !calculationError && !resultsError && (
                <RiskMetricsDisplay
                  riskMetrics={riskMetrics}
                  assetName={assetName}
                />
              )}
            </Box>
          </Box>
        )}
      </DialogContent>
      
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            color: '#00d4ad',
            borderColor: '#00d4ad',
            '&:hover': {
              borderColor: '#00b89a',
              bgcolor: 'rgba(0, 212, 173, 0.1)'
            }
          }}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetRiskReportModal;
